import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, RefreshCw, Trash2, ChevronDown, LogOut } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { SERVICE_OPTIONS } from "@/lib/siteData";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: "new", label: "New", color: "bg-[#D95D39] text-white" },
  { value: "contacted", label: "Contacted", color: "bg-[#16302B] text-white" },
  { value: "quoted", label: "Quoted", color: "bg-[#E8E2DA] text-[#16302B]" },
  { value: "closed", label: "Closed", color: "bg-[#1C1C1C] text-white" },
];

const serviceLabel = (slug) => SERVICE_OPTIONS.find((s) => s.value === slug)?.label || slug;

export default function Admin() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const { user, logout } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quotes`);
      setQuotes(res.data || []);
    } catch (e) {
      toast.error("Failed to load quote requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/quotes/${id}`, { status });
      setQuotes((prev) => prev.map((q) => (q.id === id ? res.data : q)));
      if (selected?.id === id) setSelected(res.data);
      toast.success(`Status updated to ${status}.`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quote request? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`);
      setQuotes((p) => p.filter((q) => q.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Quote deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const filtered = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);
  const counts = STATUSES.reduce(
    (acc, s) => ({ ...acc, [s.value]: quotes.filter((q) => q.status === s.value).length }),
    { all: quotes.length }
  );

  return (
    <div data-testid="admin-page" className="bg-[#F9F8F6] py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="flex flex-col items-start justify-between gap-6 border-b border-[#D1CCC5] pb-8 md:flex-row md:items-end">
          <div>
            <p className="eyebrow">Internal · Admin</p>
            <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-[#16302B] sm:text-4xl lg:text-5xl">
              Quote Requests
            </h1>
            <p className="mt-3 text-sm text-[#5C5C5C]">
              Inbound leads from the website. Click a row to view full details.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 border-2 border-[#16302B] px-5 py-3 text-xs font-bold uppercase tracking-wider text-[#16302B] transition-colors hover:bg-[#16302B] hover:text-white"
            >
              <RefreshCw size={14} strokeWidth={2.5} /> Refresh
            </button>
            <div className="flex items-center gap-3 border border-[#D1CCC5] bg-white px-4 py-2">
              {user?.picture && (
                <img src={user.picture} alt="" className="h-7 w-7 rounded-full" />
              )}
              <div className="leading-tight">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C]">Signed in</p>
                <p data-testid="admin-user-email" className="text-xs font-semibold text-[#16302B]">{user?.email}</p>
              </div>
              <button
                onClick={logout}
                data-testid="admin-logout-button"
                title="Sign out"
                className="ml-2 grid h-8 w-8 place-items-center border border-[#D1CCC5] text-[#16302B] transition-colors hover:border-[#D95D39] hover:text-[#D95D39]"
              >
                <LogOut size={14} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap gap-2">
          <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
            All <span className="ml-2 opacity-70">{counts.all || 0}</span>
          </FilterPill>
          {STATUSES.map((s) => (
            <FilterPill
              key={s.value}
              active={filter === s.value}
              onClick={() => setFilter(s.value)}
              testid={`filter-${s.value}`}
            >
              {s.label} <span className="ml-2 opacity-70">{counts[s.value] || 0}</span>
            </FilterPill>
          ))}
        </div>

        {/* Table */}
        <div className="mt-8 border border-[#D1CCC5] bg-white">
          <div className="hidden grid-cols-12 gap-4 border-b border-[#D1CCC5] bg-[#E8E2DA] px-6 py-4 text-[10px] font-bold uppercase tracking-[0.18em] text-[#16302B] md:grid">
            <div className="col-span-3">Name / Email</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-3">Service</div>
            <div className="col-span-2">Submitted</div>
            <div className="col-span-2 text-right">Status</div>
          </div>

          {loading ? (
            <div data-testid="admin-loading" className="px-6 py-16 text-center text-sm text-[#5C5C5C]">
              Loading quote requests…
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="admin-empty" className="px-6 py-16 text-center">
              <p className="font-display text-xl font-bold text-[#16302B]">No quote requests yet.</p>
              <p className="mt-2 text-sm text-[#5C5C5C]">
                Once visitors submit the form, they'll show up here.
              </p>
            </div>
          ) : (
            <ul>
              {filtered.map((q) => {
                const status = STATUSES.find((s) => s.value === q.status) || STATUSES[0];
                return (
                  <li
                    key={q.id}
                    data-testid={`quote-row-${q.id}`}
                    className="grid cursor-pointer grid-cols-1 gap-2 border-b border-[#D1CCC5] px-6 py-5 transition-colors last:border-b-0 hover:bg-[#F9F8F6] md:grid-cols-12 md:gap-4 md:py-4"
                    onClick={() => setSelected(q)}
                  >
                    <div className="md:col-span-3">
                      <p className="font-semibold text-[#16302B]">{q.name}</p>
                      <p className="text-xs text-[#5C5C5C]">{q.email}</p>
                    </div>
                    <div className="text-sm text-[#1C1C1C] md:col-span-2">{q.phone}</div>
                    <div className="text-sm text-[#1C1C1C] md:col-span-3">{serviceLabel(q.service_type)}</div>
                    <div className="text-xs text-[#5C5C5C] md:col-span-2">
                      {new Date(q.created_at).toLocaleDateString()}{" "}
                      <span className="opacity-70">
                        {new Date(q.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <div className="md:col-span-2 md:text-right">
                      <Badge className={`rounded-none px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${status.color}`}>
                        {status.label}
                      </Badge>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl rounded-none border-[#D1CCC5] bg-white p-0">
          {selected && (
            <div data-testid="quote-detail-dialog">
              <div className="border-b border-[#D1CCC5] bg-[#16302B] p-7 text-white">
                <DialogHeader>
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#D95D39]">
                    Quote Request
                  </p>
                  <DialogTitle className="mt-2 font-display text-2xl font-black tracking-tight text-white">
                    {selected.name}
                  </DialogTitle>
                  <DialogDescription className="text-white/70">
                    Submitted {new Date(selected.created_at).toLocaleString()}
                  </DialogDescription>
                </DialogHeader>
              </div>

              <div className="space-y-5 p-7">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DetailItem Icon={Mail} label="Email" value={selected.email} href={`mailto:${selected.email}`} />
                  <DetailItem Icon={Phone} label="Phone" value={selected.phone} href={`tel:${selected.phone}`} />
                  <DetailItem Icon={MapPin} label="Address" value={selected.address} />
                  <DetailItem label="Service" value={serviceLabel(selected.service_type)} />
                  <DetailItem label="Property size" value={selected.property_size || "—"} />
                  <DetailItem label="Budget" value={selected.budget || "—"} />
                  <DetailItem label="Timeline" value={selected.timeline || "—"} />
                </div>

                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C5C5C]">Project details</p>
                  <p className="mt-2 whitespace-pre-line border border-[#D1CCC5] bg-[#F9F8F6] p-4 text-sm text-[#1C1C1C]">
                    {selected.project_details}
                  </p>
                </div>

                <div className="flex flex-col items-stretch justify-between gap-4 border-t border-[#D1CCC5] pt-5 sm:flex-row sm:items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C5C5C]">Status</span>
                    <Select
                      value={selected.status}
                      onValueChange={(v) => updateStatus(selected.id, v)}
                    >
                      <SelectTrigger
                        className="w-40 rounded-none border-[#D1CCC5] focus:ring-[#D95D39]"
                        data-testid="admin-status-select"
                      >
                        <SelectValue />
                        <ChevronDown size={14} className="opacity-50" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <button
                    onClick={() => deleteQuote(selected.id)}
                    data-testid="admin-delete-quote"
                    className="inline-flex items-center justify-center gap-2 border-2 border-[#D95D39] px-5 py-3 text-xs font-bold uppercase tracking-wider text-[#D95D39] transition-colors hover:bg-[#D95D39] hover:text-white"
                  >
                    <Trash2 size={14} strokeWidth={2.5} /> Delete request
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
        active
          ? "bg-[#16302B] text-white"
          : "border border-[#D1CCC5] bg-white text-[#16302B] hover:border-[#16302B]"
      }`}
    >
      {children}
    </button>
  );
}

function DetailItem({ Icon, label, value, href }) {
  const inner = (
    <div>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C5C5C]">
        {Icon && <Icon size={12} strokeWidth={2.5} />}
        {label}
      </div>
      <p className="mt-1 text-sm font-semibold text-[#16302B]">{value}</p>
    </div>
  );
  return href ? (
    <a href={href} className="block transition-opacity hover:opacity-70">
      {inner}
    </a>
  ) : (
    inner
  );
}
